import { clsx } from 'clsx';
import {
  ELDRITCH_INVOCATIONS,
  getInvocationCount,
  getEldritchInvocation,
  meetsInvocationPrerequisites,
} from '@character-creator/data';

interface EldritchInvocationSelectorProps {
  warlockLevel: number;
  pactBoonId: string | null;
  knownCantrips: string[];
  selectedInvocations: string[];
  onToggle: (invocationId: string) => void;
  compact?: boolean;
}

export function EldritchInvocationSelector({
  warlockLevel,
  pactBoonId,
  knownCantrips,
  selectedInvocations,
  onToggle,
  compact = false,
}: EldritchInvocationSelectorProps) {
  const allowedCount = getInvocationCount(warlockLevel);
  const selectedCount = selectedInvocations.length;
  const needsMore = selectedCount < allowedCount;

  // Get selected invocation names for compact display
  const selectedNames = selectedInvocations
    .map((id) => getEldritchInvocation(id)?.name)
    .filter(Boolean);

  // Compact view - shows selected invocations
  if (compact && !needsMore && selectedNames.length > 0) {
    return (
      <div className="text-xs text-success">
        <span>Eldritch Invocations: {selectedNames.join(', ')}</span>
      </div>
    );
  }

  // Don't show if warlock isn't high enough level
  if (warlockLevel < 2) {
    return (
      <div className="text-xs text-text-muted">
        Eldritch Invocations available at level 2
      </div>
    );
  }

  // Format prerequisites for display
  const formatPrerequisites = (invocation: (typeof ELDRITCH_INVOCATIONS)[0]): string | null => {
    if (!invocation.prerequisites || invocation.prerequisites.length === 0) return null;

    const parts = invocation.prerequisites.map((prereq) => {
      switch (prereq.type) {
        case 'level':
          return `Level ${prereq.value}`;
        case 'pact': {
          const pactName = prereq.value === 'pact-of-the-blade' ? 'Pact of the Blade'
            : prereq.value === 'pact-of-the-chain' ? 'Pact of the Chain'
            : prereq.value === 'pact-of-the-tome' ? 'Pact of the Tome'
            : prereq.value;
          return pactName;
        }
        case 'cantrip':
          return prereq.value === 'eldritch-blast' ? 'eldritch blast cantrip' : prereq.value;
        default:
          return null;
      }
    }).filter(Boolean);

    return parts.length > 0 ? parts.join(', ') : null;
  };

  return (
    <div>
      <h5 className="text-sm font-medium text-text-primary mb-2">
        Eldritch Invocations
        {needsMore && (
          <span className="text-warning ml-2 text-xs">
            (Choose {allowedCount - selectedCount} more)
          </span>
        )}
      </h5>
      <p className="text-xs text-text-muted mb-3">
        In your study of occult lore, you have unearthed eldritch invocations, fragments of
        forbidden knowledge that imbue you with an abiding magical ability.
      </p>
      <div className="grid grid-cols-1 gap-2 max-h-96 overflow-y-auto pr-1">
        {ELDRITCH_INVOCATIONS.map((invocation) => {
          const isSelected = selectedInvocations.includes(invocation.id);
          const meetsPrereqs = meetsInvocationPrerequisites(
            invocation,
            warlockLevel,
            pactBoonId,
            knownCantrips
          );
          const isDisabled = !isSelected && (!meetsPrereqs || selectedCount >= allowedCount);
          const prereqText = formatPrerequisites(invocation);

          return (
            <button
              key={invocation.id}
              type="button"
              onClick={() => onToggle(invocation.id)}
              disabled={isDisabled}
              className={clsx(
                'w-full text-left p-3 rounded-lg border transition-all duration-200',
                isSelected
                  ? 'bg-accent-gold/10 border-accent-gold'
                  : isDisabled
                    ? 'bg-background-secondary border-background-elevated opacity-50 cursor-not-allowed'
                    : 'bg-background-secondary border-background-elevated hover:border-text-muted'
              )}
            >
              <div className="flex items-start justify-between gap-2">
                <span className="font-medium text-text-primary text-sm">{invocation.name}</span>
                {prereqText && (
                  <span className={clsx(
                    'text-xs shrink-0',
                    meetsPrereqs ? 'text-accent-gold' : 'text-error'
                  )}>
                    {prereqText}
                  </span>
                )}
              </div>
              <p className="text-xs text-text-muted mt-1 line-clamp-2">{invocation.description}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}
